import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  InputAdornment,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AccountBalance as BankIcon,
  AttachFile as AttachFileIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrencySymbol, getCurrencyOptions, DEFAULT_CURRENCY, formatCurrency } from '../../utils/currency';

const FraudRecordDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);

  // Call type options
  const callTypeOptions = [
    { value: 'incoming-call', label: 'Incoming call' },
    { value: 'text-message', label: 'Text message' },
    { value: 'social-media', label: 'Social media' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'other', label: 'Other' }
  ];

  // State for custom call type
  const [customCallType, setCustomCallType] = useState('');
  
  // State for editing drive links
  const [editingDriveLink, setEditingDriveLink] = useState(null);
  const [driveLinkForm, setDriveLinkForm] = useState({
    title: '',
    url: ''
  });

  // State for editing bank accounts
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [bankAccountForm, setBankAccountForm] = useState({
    accountType: 'UK',
    bankName: '',
    accountHolderName: '',
    sortCode: '',
    accountNumber: '',
    iban: '',
    routingNumber: '',
    bankAccountNumber: '',
    amount: '',
    currency: DEFAULT_CURRENCY
  });

  useEffect(() => {
    fetchRecord();
  }, [id]);

  const fetchRecord = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/fraud-records/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
      
      // Initialize custom call type if the current call type is not in the predefined options
      const predefinedTypes = callTypeOptions.map(option => option.value);
      if (response.data.callDetails?.callType && !predefinedTypes.includes(response.data.callDetails.callType)) {
        setCustomCallType(response.data.callDetails.callType);
        setFormData(prev => ({
          ...prev,
          callDetails: {
            ...prev.callDetails,
            callType: 'other'
          }
        }));
      }
    } catch (err) {
      console.error('Error fetching record:', err);
      setError('Failed to load fraud record');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleDriveLinkEdit = (link, index) => {
    setEditingDriveLink(index);
    setDriveLinkForm({
      title: link.title,
      url: link.url
    });
  };

  const handleDriveLinkSave = () => {
    if (!driveLinkForm.title || !driveLinkForm.url) {
      setError('Please fill in both title and URL for the drive link');
      return;
    }

    // Basic URL validation
    try {
      new URL(driveLinkForm.url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    const updatedLinks = [...formData.driveLinks];
    updatedLinks[editingDriveLink] = {
      title: driveLinkForm.title,
      url: driveLinkForm.url
    };

    setFormData(prev => ({
      ...prev,
      driveLinks: updatedLinks
    }));

    setEditingDriveLink(null);
    setDriveLinkForm({ title: '', url: '' });
  };

  const handleDriveLinkCancel = () => {
    setEditingDriveLink(null);
    setDriveLinkForm({ title: '', url: '' });
  };

  const handleDriveLinkDelete = (index) => {
    if (window.confirm('Are you sure you want to delete this drive link?')) {
      const updatedLinks = formData.driveLinks.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        driveLinks: updatedLinks
      }));
    }
  };

  const handleAddDriveLink = () => {
    if (!driveLinkForm.title || !driveLinkForm.url) {
      setError('Please fill in both title and URL for the drive link');
      return;
    }

    // Basic URL validation
    try {
      new URL(driveLinkForm.url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    const newLink = {
      title: driveLinkForm.title,
      url: driveLinkForm.url
    };

    setFormData(prev => ({
      ...prev,
      driveLinks: [...(prev.driveLinks || []), newLink]
    }));

    setDriveLinkForm({ title: '', url: '' });
  };

  // Bank Account handlers
  const handleBankAccountChange = (field, value) => {
    // Auto-format UK sort code
    if (field === 'sortCode') {
      const digitsOnly = value.replace(/\D/g, '');
      const limited = digitsOnly.slice(0, 6);
      let formatted = limited;
      if (limited.length > 2) {
        formatted = limited.slice(0, 2) + '-' + limited.slice(2);
      }
      if (limited.length > 4) {
        formatted = limited.slice(0, 2) + '-' + limited.slice(2, 4) + '-' + limited.slice(4);
      }
      value = formatted;
    }
    
    setBankAccountForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateBankAccount = (account) => {
    // Required fields
    if (!account.bankName || !account.accountHolderName) {
      return 'Please fill in bank name and account holder name';
    }

    // UK validation
    if (account.accountType === 'UK') {
      if (!account.sortCode) {
        return 'Sort code is required for UK accounts';
      }
      if (!/^\d{2}-\d{2}-\d{2}$/.test(account.sortCode)) {
        return 'Sort code must be in format XX-XX-XX (6 digits)';
      }
      if (!account.accountNumber) {
        return 'Account number is required for UK accounts';
      }
      if (!/^\d{8}$/.test(account.accountNumber)) {
        return 'Account number must be 8 digits for UK accounts';
      }
    }

    // US validation
    if (account.accountType === 'US') {
      if (!account.routingNumber) {
        return 'Routing number is required for US accounts';
      }
      if (!/^\d{9}$/.test(account.routingNumber)) {
        return 'Routing number must be 9 digits for US accounts';
      }
      if (!account.bankAccountNumber) {
        return 'Account number is required for US accounts';
      }
      if (!/^\d{4,17}$/.test(account.bankAccountNumber)) {
        return 'Account number must be 4-17 digits for US accounts';
      }
    }

    // EU/IBAN validation
    if (account.accountType === 'EU') {
      if (!account.iban) {
        return 'IBAN is required for EU accounts';
      }
      if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/.test(account.iban.toUpperCase().replace(/\s/g, ''))) {
        return 'Invalid IBAN format';
      }
    }

    return null; // Valid
  };

  const handleAddBankAccount = () => {
    const validationError = validateBankAccount(bankAccountForm);
    if (validationError) {
      setError(validationError);
      return;
    }

    const newAccount = {
      ...bankAccountForm,
      amount: parseFloat(bankAccountForm.amount) || 0
    };

    setFormData(prev => ({
      ...prev,
      bankAccounts: [...(prev.bankAccounts || []), newAccount]
    }));

    // Reset form
    setBankAccountForm({
      accountType: 'UK',
      bankName: '',
      accountHolderName: '',
      sortCode: '',
      accountNumber: '',
      iban: '',
      routingNumber: '',
      bankAccountNumber: '',
      amount: '',
      currency: DEFAULT_CURRENCY
    });
    setError(''); // Clear error on success
  };

  const handleEditBankAccount = (index) => {
    const account = formData.bankAccounts[index];
    setBankAccountForm({
      ...account,
      amount: account.amount?.toString() || ''
    });
    setEditingBankAccount(index);
  };

  const handleSaveBankAccount = () => {
    const validationError = validateBankAccount(bankAccountForm);
    if (validationError) {
      setError(validationError);
      return;
    }

    const updatedAccounts = [...formData.bankAccounts];
    updatedAccounts[editingBankAccount] = {
      ...bankAccountForm,
      amount: parseFloat(bankAccountForm.amount) || 0
    };

    setFormData(prev => ({
      ...prev,
      bankAccounts: updatedAccounts
    }));

    setEditingBankAccount(null);
    setBankAccountForm({
      accountType: 'UK',
      bankName: '',
      accountHolderName: '',
      sortCode: '',
      accountNumber: '',
      iban: '',
      routingNumber: '',
      bankAccountNumber: '',
      amount: '',
      currency: DEFAULT_CURRENCY
    });
    setError(''); // Clear error on success
  };

  const handleCancelBankAccountEdit = () => {
    setEditingBankAccount(null);
    setBankAccountForm({
      accountType: 'UK',
      bankName: '',
      accountHolderName: '',
      sortCode: '',
      accountNumber: '',
      iban: '',
      routingNumber: '',
      bankAccountNumber: '',
      amount: '',
      currency: DEFAULT_CURRENCY
    });
  };

  const handleDeleteBankAccount = (index) => {
    if (window.confirm('Are you sure you want to delete this bank account?')) {
      const updatedAccounts = formData.bankAccounts.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        bankAccounts: updatedAccounts
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      const dataToSend = {
        ...formData,
        callDetails: {
          ...formData.callDetails,
          totalAmount: parseFloat(formData.callDetails.totalAmount) || 0,
        },
        bankAccounts: formData.bankAccounts.map(account => ({
          ...account,
          amount: parseFloat(account.amount) || 0
        }))
      };

      await axios.put(`/api/fraud-records/${id}`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Record updated successfully!');
      setIsEditing(false);
      fetchRecord(); // Refresh data
    } catch (err) {
      console.error('Error updating record:', err);
      setError(err.response?.data?.message || 'Failed to update record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await axios.delete(`/api/fraud-records/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        navigate('/fraud-records');
      } catch (err) {
        console.error('Error deleting record:', err);
        setError('Failed to delete record');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!formData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Record not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/fraud-records')}
            sx={{ color: '#b0b0b0', mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>
            Fraud Record Details
          </Typography>
        </Box>
        <Box>
          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              sx={{
                background: 'linear-gradient(135deg, #6464ff, #8484ff)',
                color: '#ffffff',
                mr: 1
              }}
            >
              Edit
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  background: 'linear-gradient(135deg, #28aa50, #4caf50)',
                  color: '#ffffff',
                  mr: 1
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setIsEditing(false);
                  fetchRecord(); // Reset form
                }}
                sx={{ borderColor: '#666', color: '#b0b0b0' }}
              >
                Cancel
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            sx={{ 
              borderColor: '#c83232', 
              color: '#c83232',
              ml: 1,
              '&:hover': {
                borderColor: '#ff4444',
                color: '#ff4444'
              }
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, bgcolor: '#2a1a1a', color: '#ff6b6b' }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, bgcolor: '#1a2a1a', color: '#51cf66' }}>
          {success}
        </Alert>
      )}

      {/* Record Information - Shown at top when editing */}
      {isEditing && (
        <Box sx={{ mb: 3, p: 3, bgcolor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
            Record Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#bdbdbd' }}>Status</InputLabel>
                <Select
                  value={formData.status || 'new'}
                  onChange={(e) => handleInputChange('status', 'status', e.target.value)}
                  disabled={!isEditing}
                  sx={{ color: '#ffffff', bgcolor: '#2a2a2a' }}
                >
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="done">Done</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Created By"
                value={formData.createdBy?.username || 'Unknown'}
                disabled
                InputProps={{ sx: { color: '#ffffff' } }}
                InputLabelProps={{ sx: { color: '#bdbdbd' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Created Date"
                value={format(new Date(formData.createdAt), 'MMM dd, yyyy')}
                disabled
                InputProps={{ sx: { color: '#ffffff' } }}
                InputLabelProps={{ sx: { color: '#bdbdbd' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Last Updated"
                value={format(new Date(formData.updatedAt), 'MMM dd, yyyy')}
                disabled
                InputProps={{ sx: { color: '#ffffff' } }}
                InputLabelProps={{ sx: { color: '#bdbdbd' } }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Call Details Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #2a2a2a', mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 3 }}>
                Call Details
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Scam Type"
                    value={formData.callDetails?.scamType || ''}
                    onChange={(e) => handleInputChange('callDetails', 'scamType', e.target.value)}
                    disabled={!isEditing}
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#bdbdbd' }}>Contact Type</InputLabel>
                    <Select
                      value={
                        customCallType 
                          ? 'other' 
                          : callTypeOptions.find(opt => opt.value === formData.callDetails?.callType)
                            ? formData.callDetails?.callType
                            : 'other'
                      }
                      onChange={(e) => {
                        if (e.target.value !== 'other') {
                          setCustomCallType('');
                          handleInputChange('callDetails', 'callType', e.target.value);
                        } else {
                          handleInputChange('callDetails', 'callType', 'other');
                        }
                      }}
                      disabled={!isEditing}
                      sx={{ color: '#ffffff', bgcolor: '#2a2a2a' }}
                    >
                      {callTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {(customCallType || formData.callDetails?.callType === 'other' || 
                  !callTypeOptions.find(opt => opt.value === formData.callDetails?.callType)) && isEditing && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Custom Contact Type"
                      placeholder="Enter custom contact type"
                      value={customCallType || (
                        !callTypeOptions.find(opt => opt.value === formData.callDetails?.callType) 
                          ? formData.callDetails?.callType 
                          : ''
                      )}
                      onChange={(e) => {
                        setCustomCallType(e.target.value);
                        handleInputChange('callDetails', 'callType', e.target.value || 'other');
                      }}
                      disabled={!isEditing}
                      InputProps={{ sx: { color: '#ffffff' } }}
                      InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    label="Total Amount"
                    value={formData.callDetails?.totalAmount || ''}
                    onValueChange={(values) => handleInputChange('callDetails', 'totalAmount', values.value)}
                    thousandSeparator=","
                    prefix="$"
                    disabled={!isEditing}
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#bdbdbd' }}>Currency</InputLabel>
                    <Select
                      value={formData.callDetails?.currency || ''}
                      onChange={(e) => handleInputChange('callDetails', 'currency', e.target.value)}
                      disabled={!isEditing}
                      sx={{ color: '#ffffff', bgcolor: '#2a2a2a' }}
                    >
                      {getCurrencyOptions().map((currency) => (
                        <MenuItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    value={formData.callDetails?.description || ''}
                    onChange={(e) => handleInputChange('callDetails', 'description', e.target.value)}
                    disabled={!isEditing}
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contact Date"
                    type="date"
                    value={formData.callDetails?.callDate ? new Date(formData.callDetails.callDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleInputChange('callDetails', 'callDate', e.target.value)}
                    disabled={!isEditing}
                    InputLabelProps={{ shrink: true, sx: { color: '#bdbdbd' } }}
                    InputProps={{ sx: { color: '#ffffff' } }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Bank Accounts Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #2a2a2a', mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}>
                <BankIcon sx={{ mr: 1 }} /> Bank Accounts
              </Typography>
              
              {/* Bank Account Form (Add/Edit) */}
              {isEditing && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#2a2a2a', borderRadius: 1 }}>
                  {editingBankAccount !== null && (
                    <Typography variant="subtitle2" sx={{ color: '#6464ff', mb: 2 }}>
                      Editing Bank Account
                    </Typography>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#bdbdbd' }}>Account Type</InputLabel>
                        <Select
                          value={bankAccountForm.accountType}
                          onChange={(e) => handleBankAccountChange('accountType', e.target.value)}
                          sx={{ color: '#ffffff', bgcolor: '#1a1a1a' }}
                        >
                          <MenuItem value="UK">UK</MenuItem>
                          <MenuItem value="US">US</MenuItem>
                          <MenuItem value="EU">EU</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Bank Name *"
                        value={bankAccountForm.bankName}
                        onChange={(e) => handleBankAccountChange('bankName', e.target.value)}
                        InputProps={{ sx: { color: '#ffffff' } }}
                        InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Account Holder Name *"
                        value={bankAccountForm.accountHolderName}
                        onChange={(e) => handleBankAccountChange('accountHolderName', e.target.value)}
                        InputProps={{ sx: { color: '#ffffff' } }}
                        InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                      />
                    </Grid>
                    {bankAccountForm.accountType === 'UK' && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Sort Code"
                            placeholder="12-34-56 or 123456"
                            value={bankAccountForm.sortCode}
                            onChange={(e) => handleBankAccountChange('sortCode', e.target.value)}
                            InputProps={{ sx: { color: '#ffffff' } }}
                            InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                            helperText="Enter 6 digits (auto-formatted)"
                            FormHelperTextProps={{ sx: { color: '#888' } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Account Number"
                            value={bankAccountForm.accountNumber}
                            onChange={(e) => handleBankAccountChange('accountNumber', e.target.value)}
                            InputProps={{ sx: { color: '#ffffff' } }}
                            InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                          />
                        </Grid>
                      </>
                    )}
                    {bankAccountForm.accountType === 'US' && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Routing Number"
                            value={bankAccountForm.routingNumber}
                            onChange={(e) => handleBankAccountChange('routingNumber', e.target.value)}
                            InputProps={{ sx: { color: '#ffffff' } }}
                            InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Account Number"
                            value={bankAccountForm.bankAccountNumber}
                            onChange={(e) => handleBankAccountChange('bankAccountNumber', e.target.value)}
                            InputProps={{ sx: { color: '#ffffff' } }}
                            InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                          />
                        </Grid>
                      </>
                    )}
                    {bankAccountForm.accountType === 'EU' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="IBAN"
                          value={bankAccountForm.iban}
                          onChange={(e) => handleBankAccountChange('iban', e.target.value)}
                          InputProps={{ sx: { color: '#ffffff' } }}
                          InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <NumericFormat
                        customInput={TextField}
                        fullWidth
                        label="Amount"
                        value={bankAccountForm.amount}
                        onValueChange={(values) => handleBankAccountChange('amount', values.value)}
                        thousandSeparator=","
                        prefix={getCurrencySymbol(bankAccountForm.currency)}
                        InputProps={{ sx: { color: '#ffffff' } }}
                        InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#bdbdbd' }}>Currency</InputLabel>
                        <Select
                          value={bankAccountForm.currency}
                          onChange={(e) => handleBankAccountChange('currency', e.target.value)}
                          sx={{ color: '#ffffff', bgcolor: '#1a1a1a' }}
                        >
                          {getCurrencyOptions().map((currency) => (
                            <MenuItem key={currency.value} value={currency.value}>
                              {currency.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      {editingBankAccount !== null ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            onClick={handleSaveBankAccount}
                            startIcon={<SaveIcon />}
                            sx={{ bgcolor: '#6464ff', '&:hover': { bgcolor: '#5252cc' } }}
                          >
                            Save Changes
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handleCancelBankAccountEdit}
                            startIcon={<CancelIcon />}
                            sx={{ borderColor: '#757575', color: '#757575' }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined"
                          onClick={handleAddBankAccount}
                          startIcon={<AddIcon />}
                          sx={{ borderColor: '#6464ff', color: '#6464ff' }}
                        >
                          Add Bank Account
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Bank Accounts List */}
              {formData.bankAccounts && formData.bankAccounts.length > 0 ? (
                <List>
                  {formData.bankAccounts.map((account, index) => (
                    <ListItem key={index} sx={{ bgcolor: '#2a2a2a', mb: 2, borderRadius: 1 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 500 }}>
                            {account.bankName} - {account.accountHolderName}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                              {account.accountType} • {formatCurrency(account.amount, account.currency)}
                            </Typography>
                            {account.sortCode && (
                              <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                                Sort Code: {account.sortCode} • Account: {account.accountNumber}
                              </Typography>
                            )}
                            {account.routingNumber && (
                              <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                                Routing: {account.routingNumber} • Account: {account.bankAccountNumber}
                              </Typography>
                            )}
                            {account.iban && (
                              <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                                IBAN: {account.iban}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      {isEditing && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleEditBankAccount(index)}
                            disabled={editingBankAccount !== null}
                            sx={{ color: '#6464ff', mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteBankAccount(index)}
                            disabled={editingBankAccount !== null}
                            sx={{ color: '#ff6464' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography sx={{ color: '#bdbdbd', fontStyle: 'italic' }}>
                  No bank accounts added.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>


        {/* Drive Links Section */}
        <Grid item xs={12}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LinkIcon sx={{ color: '#757575', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                  Evidence Links (Google Drive)
                </Typography>
              </Box>
              {isEditing && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setEditingDriveLink('new')}
                  sx={{ borderColor: '#6464ff', color: '#6464ff' }}
                >
                  Add Link
                </Button>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ color: '#ffffff', mb: 1 }}>
                  Drive Links ({formData.driveLinks?.length || 0})
                </Typography>
                
                {/* Add/Edit Drive Link Form */}
                {isEditing && editingDriveLink !== null && (
                  <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #2a2a2a', mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ color: '#ffffff', mb: 2 }}>
                        {editingDriveLink === 'new' ? 'Add New Drive Link' : 'Edit Drive Link'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Link Title"
                            value={driveLinkForm.title}
                            onChange={(e) => setDriveLinkForm(prev => ({ ...prev, title: e.target.value }))}
                            InputProps={{ sx: { color: '#ffffff' } }}
                            InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Google Drive URL"
                            value={driveLinkForm.url}
                            onChange={(e) => setDriveLinkForm(prev => ({ ...prev, url: e.target.value }))}
                            InputProps={{ sx: { color: '#ffffff' } }}
                            InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              onClick={editingDriveLink === 'new' ? handleAddDriveLink : handleDriveLinkSave}
                              sx={{ bgcolor: '#6464ff', color: '#ffffff' }}
                            >
                              {editingDriveLink === 'new' ? 'Add Link' : 'Save Changes'}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={handleDriveLinkCancel}
                              sx={{ borderColor: '#666', color: '#b0b0b0' }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Drive Links List */}
                {formData.driveLinks && formData.driveLinks.length > 0 ? (
                  <List dense sx={{ bgcolor: '#2a2a2a', p: 0 }}>
                    {formData.driveLinks.map((link, index) => (
                      <ListItem key={index} sx={{ borderBottom: '1px solid #444' }}>
                        <ListItemIcon sx={{ minWidth: '40px' }}>
                          <LinkIcon sx={{ color: '#6464ff' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={link.title}
                          secondary={
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#6464ff', 
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                '&:hover': { color: '#8484ff' }
                              }}
                              onClick={() => window.open(link.url, '_blank')}
                            >
                              {link.url}
                            </Typography>
                          }
                          sx={{
                            '& .MuiListItemText-primary': { color: '#ffffff', fontSize: '0.875rem' },
                            '& .MuiListItemText-secondary': { color: '#bdbdbd', fontSize: '0.75rem' }
                          }}
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {isEditing && (
                              <>
                                <IconButton
                                  onClick={() => handleDriveLinkEdit(link, index)}
                                  sx={{ color: '#6464ff' }}
                                  size="small"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDriveLinkDelete(index)}
                                  sx={{ color: '#c83232' }}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            )}
                            <IconButton
                              onClick={() => window.open(link.url, '_blank')}
                              sx={{ color: '#6464ff' }}
                              size="small"
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography sx={{ color: '#bdbdbd', fontStyle: 'italic', fontSize: '0.875rem' }}>
                    No drive links added yet.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Metadata Section - Shown at bottom when not editing */}
        {!isEditing && (
          <Grid item xs={12}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                Record Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#bdbdbd' }}>Status</InputLabel>
                    <Select
                      value={formData.status || 'new'}
                      onChange={(e) => handleInputChange('status', 'status', e.target.value)}
                      disabled={!isEditing}
                      sx={{ color: '#ffffff', bgcolor: '#2a2a2a' }}
                    >
                      <MenuItem value="new">New</MenuItem>
                      <MenuItem value="in-progress">In Progress</MenuItem>
                      <MenuItem value="done">Done</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Created By"
                    value={formData.createdBy?.username || 'Unknown'}
                    disabled
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Created Date"
                    value={format(new Date(formData.createdAt), 'MMM dd, yyyy')}
                    disabled
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Last Updated"
                    value={format(new Date(formData.updatedAt), 'MMM dd, yyyy')}
                    disabled
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default FraudRecordDetail;
